import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { useTheme } from '../../theme';
// Create a style element for the spinner animation
const injectSpinnerStyles = () => {
    if (typeof document === 'undefined')
        return;
    const styleId = 'button-spinner-keyframes';
    if (document.getElementById(styleId))
        return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
    @keyframes button-spin {
      to {
        transform: rotate(360deg);
      }
    }
  `;
    document.head.appendChild(style);
};
// Inject styles when module loads
if (typeof window !== 'undefined') {
    injectSpinnerStyles();
}
export const Button = forwardRef(({ variant = 'primary', size = 'md', loading = false, iconBefore, iconAfter, fullWidth = false, disabled, children, style, ...props }, ref) => {
    const theme = useTheme();
    const getButtonStyles = () => {
        const baseStyles = {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing.sm,
            border: 'none',
            borderRadius: theme.borderRadius.md,
            fontFamily: theme.typography.fontFamily,
            fontWeight: theme.typography.fontWeight.medium,
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            opacity: disabled || loading ? 0.6 : 1,
            transition: 'all 0.2s ease',
            textDecoration: 'none',
            outline: 'none',
            position: 'relative',
            width: fullWidth ? '100%' : 'auto',
        };
        // Size styles
        const sizeStyles = {
            sm: {
                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                fontSize: theme.typography.fontSize.small,
            },
            md: {
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.body,
            },
            lg: {
                padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                fontSize: theme.typography.fontSize.h6,
            },
        };
        // Variant styles
        const variantStyles = {
            primary: {
                backgroundColor: theme.colors.primary,
                color: 'white',
            },
            secondary: {
                backgroundColor: theme.colors.surface,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
            },
            outline: {
                backgroundColor: 'transparent',
                color: theme.colors.primary,
                border: `1px solid ${theme.colors.primary}`,
            },
            ghost: {
                backgroundColor: 'transparent',
                color: theme.colors.text,
            },
            danger: {
                backgroundColor: theme.colors.error,
                color: 'white',
            },
        };
        return {
            ...baseStyles,
            ...sizeStyles[size],
            ...variantStyles[variant],
            ...style,
        };
    };
    const getHoverStyles = () => {
        const hoverStyles = {
            primary: {
                backgroundColor: theme.colors.secondary,
            },
            secondary: {
                backgroundColor: theme.colors.background,
            },
            outline: {
                backgroundColor: theme.colors.primary,
                color: 'white',
            },
            ghost: {
                backgroundColor: theme.colors.surface,
            },
            danger: {
                backgroundColor: '#c82333',
            },
        };
        return hoverStyles[variant];
    };
    return (_jsxs("button", { ref: ref, disabled: disabled || loading, style: getButtonStyles(), onMouseEnter: (e) => {
            if (!disabled && !loading) {
                Object.assign(e.currentTarget.style, getHoverStyles());
            }
        }, onMouseLeave: (e) => {
            if (!disabled && !loading) {
                Object.assign(e.currentTarget.style, getButtonStyles());
            }
        }, ...props, children: [loading ? (_jsx("span", { style: {
                    width: '1em',
                    height: '1em',
                    border: '2px solid transparent',
                    borderTop: '2px solid currentColor',
                    borderRadius: '50%',
                    animation: 'button-spin 1s linear infinite',
                } })) : (iconBefore), children, !loading && iconAfter] }));
});
Button.displayName = 'Button';
